import type { ReactNode } from 'react';
import type { DashboardTileDataOf, DashboardTileId } from '@pams/shared';
import { readableDate } from '@pams/shared';
import { fillBadge, linesUnnamedFigure, namedOf, orderStatusWord, ordersByStatusRows } from '@/lib/statusPresentation';
import { FigureList, RankedList } from '@/components/DataFigures';
import { StatusDot } from '@/components/StatusDot';
import { PreviewEye } from '@/components/preview/ArrangementPreviewModal';
import { TILE_SCROLL, TILE_VISIBLE_ROWS } from '@/lib/dashboardGrid';
import { DASHBOARD_COPY, TILE_COPY } from '../copy';
import type { DrillReach } from '../TileCard';
import { BigNumber, BigNumberSkeleton, DutyRows, ListSkeleton, TileEmpty } from './parts';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ONE RENDERER PER TILE, AS DATA. [P6a]
 *
 *  `lib/sectionRegistry.ts`'s doctrine — a screen's parts are DATA, not JSX position —
 *  applied a second time. The DECLARATION is `packages/shared`; the SERVER WIRING is
 *  `api/src/services/dashboardRegistry.service.ts`; this is the third `Record` over the same
 *  id union, so a tile that exists with no renderer is a COMPILE ERROR rather than a blank
 *  card, and a renderer whose tile was retired is a dead entry the compiler names.
 *
 *  ⚠️ THE PAGE RENDERS `payload.tiles` IN THE SERVER'S ORDER AND NEVER ITERATES THIS RECORD.
 *  That is what makes FR-3.6 hold on this surface: a tile outside the caller's scope is
 *  ABSENT rather than present-and-empty, and the client has no mechanism to invent one. It is
 *  also what makes the "absent, not empty" test mean something instead of being a tautology.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C1e §1a] WHAT A RENDERER MAY DO BESIDES DRAW, AND IT IS AN ARGUMENT RATHER THAN A CONTEXT.
 *
 * `ARRANGEMENTS_TODAY` puts an eye on every row that opens the quick preview, so a renderer
 * needs a channel to the host. Widening `render` costs ONE LINE and nothing else: a function of
 * fewer parameters is assignable to one of more, so the other eighteen renderers are untouched
 * and untouchable by this.
 *
 * ⚠️ A REACT CONTEXT WAS THE ALTERNATIVE AND IS WORSE HERE. `renderTile` has exactly one caller,
 * so a context would be a global standing in for a prop — and a missing Provider is a runtime
 * `undefined` where a missing argument is a compile error. The registry's own header says a
 * renderer is DATA; an explicit, typed parameter keeps it so.
 */
export type TileActions = {
  readonly onPreview: (arrangementId: string, title: string, eyeKey: string) => void;
};

export type TileRenderer<K extends DashboardTileId> = {
  readonly drillReach: DrillReach;
  readonly render: (data: DashboardTileDataOf<K>, actions: TileActions) => ReactNode;
  readonly skeleton: () => ReactNode;
  /**
   * ⚠️ A LINK TO A LIST THAT IS GUARANTEED EMPTY IS A PROMISE THE NEXT CLICK BREAKS.
   *
   * §4's rule. A red count of zero must not open an inbox filtered to unnamed lines and show
   * nothing — the title renders as PLAIN TEXT instead, which is a real state rather than a
   * missing prop. Omitted means always drillable, which is right for a tile whose destination
   * is a list of orders that exists whatever this number says.
   */
  readonly drillable?: (data: DashboardTileDataOf<K>) => boolean;
};

export const TILE_RENDERERS: { [K in DashboardTileId]: TileRenderer<K> } = {
  ORDERS_BY_STATUS: {
    /* ⚠️ Its rows carry no links of their own, so the whole card may be the target. */
    drillReach: 'card',
    skeleton: () => <ListSkeleton rows={4} />,
    /*
     * ⚠️ [C5b §3] FOLDED BY WORD. `PUBLISHED` and `ALLOCATION_IN_PROGRESS` both read `Published`
     * now, so two rows would say `Published` twice with two numbers (D12) — and the second one
     * used to say `Partially Filled`, a fill claim on a card that counts lifecycle stages.
     */
    render: (data) => (
      <FigureList
        rows={ordersByStatusRows(data.byStatus).map((row) => ({
          key: row.word,
          label: <StatusDot tone={row.tone} label={row.label} />,
          value: String(row.count),
        }))}
      />
    ),
  },

  /**
   * ⚠️ ZERO DOES NOT MEAN THE SAME THING AS A COUNT, so it does not say the same thing.
   * `FILL_WORD_TONE.NOT_NAMED` is `danger` and its reason applies verbatim here: an
   * unanswered request from another office is neither a draft nor a pending duty. At zero the
   * tone is `success` and the sentence changes — colour must never call a finished job a
   * problem.
   *
   * ⚠️ [amended C5b §3] AND ZERO OVER NO ASK IS A THIRD THING. `Every line is named` in green over
   * a range that asked for nobody is an achievement claimed before any work existed (D15); it
   * reads `No force asked for` in neutral. `linesUnnamedFigure` holds all three arms.
   */
  LINES_UNNAMED: {
    drillReach: 'card',
    skeleton: () => <BigNumberSkeleton />,
    drillable: (data) => data.unnamedSlots > 0,
    render: (data) => {
      const figure = linesUnnamedFigure(data);
      return <BigNumber value={data.unnamedSlots} unit={TILE_COPY.slots} tone={figure.tone} label={figure.label} />;
    },
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [amended C2b §6a] ONE ROW PER ARRANGEMENT, FIXED HEIGHT, INNER SCROLL.
   *
   *  It was one `NestedBar` over the whole commissionerate. That says how far behind the office
   *  is and never WHICH order to ring a division about, which is the only action it supports.
   *
   *  ⚠️ `drillReach: 'footer'` NOW, AND THAT IS FORCED. A stretched `after:absolute` overlay
   *  over a scrolling list makes its text unselectable and its rows unreachable — [C1e §1a]'s
   *  rule, the same one `ARRANGEMENTS_TODAY` and `DUTIES_UNSTAFFED` obey.
   *
   *  ⚠️ `TILE_SCROLL` IS A LITERAL CLASS, pinned by set equality to `TILE_ROW_PX ×
   *  TILE_VISIBLE_ROWS`. An interpolated `max-h-[${n}px]` generates NOTHING and the tile grows
   *  to fit with `tsc`, `eslint` and `vitest` all green — `grid-cols-${n}`'s trap.
   *  ⚠️ AND NO `overscroll-contain`: [C1e §1a] — `contain` blocks chaining AT THE BOUNDARY,
   *  which is exactly trapping the wheel at either end of a list sitting in page flow.
   *
   *  ⚠️ THE WINDOW LABEL IS RENDERED, BECAUSE THIS IS THE ONE TILE THAT IGNORES THE RANGE.
   *  It comes from the PAYLOAD rather than the registry hint: [C1e §3] made the hint
   *  operator-overridable, so a structural claim there can be edited away by the person it is
   *  protecting.
   *
   *  ⚠️ AND THE TRUNCATION SAYS SO. A silently shortened list reads as "that is all there is",
   *  which on this card is the difference between an order somebody rings about and one nobody
   *  does.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  REQUIREMENT_FULFILMENT: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={3} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">{data.windowLabel}</p>
          <div className={TILE_SCROLL} data-testid="fulfilment-rows">
            <RankedList
              rows={data.rows.map((row) => ({
                key: row.arrangementId,
                /*
                 * ⚠️ [TODAY-TILE] DUTY DATE · PA NUMBER · TITLE, EACH OMITTED WHEN ABSENT.
                 *
                 * The date LEADS because this card covers two days and said which nowhere on the
                 * row — two orders a day apart read as one backlog, and the whole action this
                 * tile supports is deciding which division to ring TONIGHT.
                 *
                 * ⚠️ IT IS THE ROW'S OWN `dutyDate` AND NOT `order.pa_date`. [D4]: `pa_date` is
                 * ADMINISTRATIVE ("the order was dated X") and `duty_date` is OPERATIONAL
                 * ("there is duty on X"), and a card about tonight's staffing means the second.
                 * ⚠️ `null` IS REACHABLE — a row survives on `required > 0 || allocated > 0`, so
                 * an order with fills whose duties are all archived has no date to state — and
                 * the part is dropped rather than rendering a placeholder.
                 *
                 * ⚠️ AND THE PA NUMBER STAYS `paNumber`, never `reference`: [C3c.2]'s rule, that
                 * a system-minted id must not sit in the position of a number typed on paper.
                 */
                label: [
                  row.dutyDate === null ? null : (readableDate(row.dutyDate) ?? row.dutyDate),
                  row.paNumber,
                  row.displayTitle,
                ]
                  .filter((part) => part !== null && part !== '')
                  .join(' · '),
                value: row.namedSlots,
                caption: TILE_COPY.fulfilment.ofRequired(row.required),
                /* ⚠️ [C3c.2] The order's own record page — `ArrangementView`, which [C3a §4] made
                   the record page and which every other list links a name to. */
                href: `/arrangements/${row.arrangementId}`,
              }))}
            />
          </div>
          {data.truncated > 0 ? (
            <p className="text-xs text-muted-foreground">{TILE_COPY.fulfilment.more(data.truncated)}</p>
          ) : null}
        </div>
      ),
  },

  DUTIES_UNSTAFFED: {
    /* ⚠️ Its body is a list of orders, so the drill lives in the FOOTER — a stretched
       overlay would make every row unreachable. */
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={3} />,
    drillable: (data) => data.total > 0,
    render: (data) =>
      data.total === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <div className="flex flex-col gap-2">
          <BigNumber value={data.total} unit={TILE_COPY.duties} tone="danger" label="with nobody named" />
          <FigureList
            rows={data.rows.slice(0, 4).map((row) => ({
              key: row.deploymentId,
              label: `${row.reference} · ${row.dutyDate}`,
              value: String(row.allocatedSlots),
            }))}
          />
        </div>
      ),
  },

  ATTENDANCE: {
    drillReach: 'card',
    skeleton: () => <ListSkeleton rows={3} />,
    render: (data) =>
      data.onDuty === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={[
            { key: 'reached', label: TILE_COPY.attendance.reached, value: String(data.reached) },
            { key: 'notReached', label: TILE_COPY.attendance.notReached, value: String(data.notReached) },
            { key: 'absent', label: TILE_COPY.attendance.absent, value: String(data.absent) },
          ]}
        />
      ),
  },

  SWAPS: {
    drillReach: 'card',
    skeleton: () => <BigNumberSkeleton />,
    render: (data) => (
      <BigNumber
        value={data.swapsPerformed}
        unit={TILE_COPY.swaps}
        tone={data.swapsPerformed > 0 ? 'warning' : 'neutral'}
        label={data.swapsPerformed > 0 ? 'after the order was issued' : 'none in range'}
      />
    ),
  },

  /**
   * ⚠️ [C1e §1a] `drillReach: 'footer'`, AND IT IS LOAD-BEARING. `TileCard`'s stretched
   * `after:absolute after:inset-0` would sit over every eye in the list and make the rows'
   * text unselectable — which is exactly the case that flag was declared for.
   */
  /**
   * ⚠️ [C1g §1e] THE BELL'S TWIN, AND `drillReach: 'footer'` FOR `ARRANGEMENTS_TODAY`'s REASON
   * — this body is a LIST, and a stretched card overlay makes list text unselectable.
   *
   * ⚠️ ONE `StatusDot` PER ROW AND NO OTHER COLOUR, which is `FillLines`' rule one screen over.
   * A row is one ORDER, so "3 of 12 named" is the whole order's progress across every duty it
   * still owes — never a per-duty figure, which is `ARRANGEMENTS_TODAY`'s question.
   */
  ORDERS_TO_FILL: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={TILE_VISIBLE_ROWS} />,
    drillable: (data) => data.rows.length > 0,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.nothingToFill}</TileEmpty>
      ) : (
        <DutyRows
          rows={data.rows.map((row) => ({
            key: row.arrangementId,
            dutyPoint: row.displayTitle,
            when: TILE_COPY.dutyCount(row.dutyCount),
            /* ⚠️ [C5b review] `namedOf`, the one spelling — `TILE_COPY` held a copy of it. */
            detail: namedOf(row.namedSlots, row.allocatedSlots),
            /* ⚠️ [C5b §3] `closedShort: false` — this tile never holds a COMPLETE order. */
            state: fillBadge({ fillState: row.fillState, closedShort: false, allocatedCount: row.allocatedSlots }),
          }))}
        />
      ),
  },

  ARRANGEMENTS_TODAY: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={TILE_VISIBLE_ROWS} />,
    drillable: (data) => data.rows.length > 0,
    render: (data, actions) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noDutiesToday}</TileEmpty>
      ) : (
        <div className="flex flex-col gap-2">
          {/* ⚠️ [TODAY-TILE] THE WINDOW, FOR `REQUIREMENT_FULFILMENT`'s REASON — this tile now
              ignores the dashboard's From/To and shows TODAY, so an unlabelled exception reads
              as a bug. From the PAYLOAD, never the registry hint, which [C1e §3] made
              operator-overridable. */}
          <p className="text-xs text-muted-foreground">{data.windowLabel}</p>
          <DutyRows
            rows={data.rows.map((row) => ({
              key: row.deploymentId,
              /*
               * ⚠️ [C3c.2] THE PA ORDER NUMBER LEADS, AND IT IS OMITTED WHEN THERE IS NONE.
               * `paNumber` is what is typed on the paper order; `row.reference` beside it already
               * collapses `pa_number ?? reference`, so using THAT would put a system-minted id in
               * the same position as a real order number — the Inbox chip's defect, one screen over.
               */
              dutyPoint: [row.paNumber, `DP${String(row.dutyPointOrdinal)}`, row.dutyPointTitle]
                .filter((part) => part !== null && part !== '')
                .join(' · '),
              /*
               * ⚠️ [C3c.2] STRAIGHT TO THE ASSIGN PA SCREEN FOR THIS DUTY POINT, AND WITH NO
               * `?division=`.
               *
               * The inbox appends one because an inbox row IS one division's share, and without it
               * a super admin's `Mark complete` would close a division they were not looking at.
               * This row is the WHOLE duty — `addressees` is a list, and the row renders every one
               * of their short codes — so there is no single division it could name. `DutyFill`
               * reads the parameter as `searchParams.get('division') ?? undefined` and renders the
               * unnarrowed sheet without it, which is exactly the sheet this row describes.
               */
              href: `/duties/${row.deploymentId}`,
              when: `${readableDate(row.dutyDate) ?? row.dutyDate} · ${row.timeLabel}`,
              /*
               * ⚠️ [C1e §1a] "units named" IS THE ADDRESSEE LIST — who owes the force here.
               *
               * Short codes shown, full names in a native `title`. NOT `AddresseeCodes`: that
               * takes a `(division, unit)` PAIR because an inbox row is one division's share,
               * and this row is a whole duty with several. And a native `title` adds no overlay
               * site — divergence #82 measured what a `Tooltip` per list row costs.
               */
              detail: (
                <span title={row.addressees.map((unit) => unit.name).join(', ')}>
                  {row.addressees.map((unit) => unit.shortCode).join(', ')}
                </span>
              ),
              /* ⚠️ [C5b §3] Reads the row's `closedShort` — S07 read `All Filled` here (D4). */
              state: fillBadge({
                fillState: row.fillState,
                closedShort: row.closedShort,
                allocatedCount: row.allocatedSlots,
              }),
              action: (
                <PreviewEye
                  eyeKey={row.deploymentId}
                  title={row.displayTitle}
                  onOpen={() => {
                    actions.onPreview(row.arrangementId, row.displayTitle, row.deploymentId);
                  }}
                />
              ),
            }))}
          />
          {/* ⚠️ OUTSIDE THE SCROLLER, so "there are more" cannot scroll away. */}
          {data.total > data.rows.length ? (
            <p className="relative z-10 text-xs text-muted-foreground">
              {DASHBOARD_COPY.truncated(data.rows.length, data.total)}
            </p>
          ) : null}
        </div>
      ),
  },

  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [ASSOC-TILE] `drillReach: 'footer'`, FOR `ARRANGEMENTS_TODAY`'s REASON EXACTLY.
   *
   *  The body is a LIST whose names are anchors, and `TileCard`'s stretched
   *  `after:absolute after:inset-0` under `'card'` would sit on top of every one of them —
   *  [C2b §6a.1] states that a row link is only safe on a footer-reach tile, and this is the
   *  condition it attaches.
   *
   *  ⚠️ THE ROW NAME LINKS TO `/arrangements/:id` — THE READ-ONLY RECORD PAGE, AND THAT IS THE
   *  WHOLE FEATURE. `ArrangementViewPage` renders behind `arrangement.read`, which every
   *  dashboard role holds, and every action on it is already withheld from a role that cannot
   *  perform it. So a `NODAL_READ` gets the order, its duty points and who is named on them,
   *  and no control that would refuse.
   *
   *  ⚠️ AND THERE IS NO `PreviewEye`. The preview modal is a SECOND route to the same record,
   *  which is exactly the "two ways to one destination" `NameCell`'s rule forbids — and the
   *  point of this card is to open the page, not to glance at it. `ARRANGEMENTS_TODAY` earns
   *  its eye because its own name goes to `/duties/:id` instead, so the eye is the only route
   *  it has to the ORDER.
   *
   *  ⚠️ ONE `StatusDot` PER ROW AND NO OTHER COLOUR — `FillLines`' rule, which `ORDERS_TO_FILL`
   *  states one entry above. The tone is the ORDER's own lifecycle status, never a fill state:
   *  this unit has no share on these orders ([C1b §4]), so a red-amber-green fill band here
   *  would be somebody else's progress rendered on this unit's card.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  ASSOCIATED_ORDERS: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={TILE_VISIBLE_ROWS} />,
    drillable: (data) => data.rows.length > 0,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noAssociatedOrders}</TileEmpty>
      ) : (
        <div className="flex flex-col gap-2">
          <DutyRows
            rows={data.rows.map((row) => ({
              key: row.arrangementId,
              /*
               * ⚠️ [C3c.2] THE PA ORDER NUMBER LEADS AND IS OMITTED WHEN THERE IS NONE.
               * `row.reference` already collapses `pa_number ?? reference`, so using THAT here
               * would put a system-minted id in the position an operator reads as the number
               * written on the paper order — the Inbox chip's defect, two screens over.
               */
              dutyPoint: [row.paNumber, row.displayTitle].filter((part) => part !== null).join(' · '),
              href: `/arrangements/${row.arrangementId}`,
              /*
               * ⚠️ THE ORDER DATE, WHICH IS ADMINISTRATIVE ([D4]) — what the order is HEADED
               * with and what an operator matches against the paper in their hand. The
               * operational span is on the drill-through table, where there is room for both.
               */
              when: readableDate(row.paDate) ?? row.paDate,
              detail: TILE_COPY.dutySpan(row.firstDutyDate, row.lastDutyDate),
              /* ⚠️ [C5b §3] The lifecycle WORD. The spine is current orders only, so no archive. */
              state: orderStatusWord({ status: row.status, archivedAt: null }),
            }))}
          />
          {/* ⚠️ OUTSIDE THE SCROLLER, so "there are more" cannot scroll away. */}
          {data.total > data.rows.length ? (
            <p className="relative z-10 text-xs text-muted-foreground">
              {DASHBOARD_COPY.truncated(data.rows.length, data.total)}
            </p>
          ) : null}
        </div>
      ),
  },

  DIVISION_ROLLUP: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={5} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={data.rows.slice(0, 6).map((row) => ({
            key: row.orgUnitId,
            label: row.orgUnit?.name ?? row.orgUnitId,
            /* Asked of them · named · still to name — the division's own three numbers. */
            value: `${String(row.allocatedSlots)} · ${String(row.namedSlots)} · ${String(row.stillToName)}`,
          }))}
        />
      ),
  },

  LOAD_BY_DIVISION: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={5} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <RankedList
          rows={data.rows.slice(0, 6).map((row) => ({
            key: row.orgUnitId,
            label: row.orgUnit?.name ?? row.orgUnitId,
            value: row.allocatedSlots,
            caption: `/ ${String(row.namedSlots)}`,
          }))}
        />
      ),
  },

  LOAD_BY_UNIT: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={5} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <RankedList
          rows={data.rows.slice(0, 6).map((row) => ({
            key: row.orgUnitId,
            label: row.orgUnit?.name ?? row.orgUnitId,
            value: row.allocatedSlots,
            caption: `/ ${String(row.namedSlots)}`,
          }))}
        />
      ),
  },

  REPEAT_DEPLOYMENT: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={5} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <RankedList
          rows={data.rows.slice(0, 6).map((row) => ({
            key: row.officerId,
            label: row.rankShortName ? `${row.rankShortName} ${row.name}` : row.name,
            value: row.duties,
          }))}
        />
      ),
  },

  PUBLISH_TO_COMPLETE: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={4} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={data.rows.slice(0, 5).map((row) => ({
            key: row.orgUnitId,
            label: row.orgUnit?.name ?? row.orgUnitId,
            value: `${String(row.medianHours)} h`,
          }))}
        />
      ),
  },

  LATE_START: {
    drillReach: 'card',
    skeleton: () => <BigNumberSkeleton />,
    render: (data) => (
      <BigNumber
        value={data.late}
        unit={`of ${String(data.started)} started`}
        tone={data.late > 0 ? 'warning' : 'success'}
        label={`more than ${String(data.graceMinutes)} minutes late`}
      />
    ),
  },

  VEHICLE_FULFILMENT: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={4} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={data.rows.slice(0, 5).map((row) => ({
            key: row.vehicleTypeId,
            label: row.name,
            /* ⚠️ [P7A §3] `2 required` — never `sourced / required`, which is a vehicle short-fall. */
            value: `${String(row.required)} required`,
          }))}
        />
      ),
  },

  ORDERS_BY_POLICY: {
    drillReach: 'footer',
    skeleton: () => <ListSkeleton rows={4} />,
    render: (data) =>
      data.rows.length === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={data.rows.slice(0, 5).map((row) => ({
            key: row.policyId,
            label: row.name,
            value: String(row.orders),
          }))}
        />
      ),
  },

  /*
   * ⚠️ [P6b.1] A SHORTCUT HAS NO BODY, AND THAT IS THE WHOLE CARD.
   *
   * `TileCard` already renders the title as the link, the hint beneath it and the drill label
   * at the foot — which is exactly a shortcut: a heading, a subtitle and a way in. Inventing
   * a body would be decoration on a card whose entire content is its destination.
   *
   * ⚠️ `drillReach: 'card'`, so the whole rectangle is the hit area. A navigation card the
   * operator has to hit a 12px title on is worse than the sidebar row it replaces.
   */
  SHORTCUT: { drillReach: 'card', render: () => null, skeleton: () => <BigNumberSkeleton /> },
  STANDING_ORDER_COVERAGE: {
    drillReach: 'card',
    skeleton: () => <ListSkeleton rows={3} />,
    render: (data) =>
      data.activeOrders === 0 ? (
        <TileEmpty>{TILE_COPY.noRows}</TileEmpty>
      ) : (
        <FigureList
          rows={[
            { key: 'orders', label: 'Standing orders live', value: String(data.activeOrders) },
            { key: 'due', label: 'Nights due', value: String(data.expectedDays) },
            { key: 'raised', label: 'Nights raised', value: String(data.generatedDays) },
          ]}
        />
      ),
  },
};

/**
 * ⚠️ THE ONE CAST ON THIS SIDE, AND IT IS AT A DYNAMIC INDEX. `TILE_RENDERERS[id]` for an `id`
 * only known to be SOME tile yields a union of nineteen `render` signatures, and no caller can
 * invoke a union of functions. The per-key record above has already done its work by then —
 * the payload the page hands in is discriminated on the same id.
 */
export const renderTile = (id: DashboardTileId, data: unknown, actions: TileActions): ReactNode =>
  (TILE_RENDERERS[id] as TileRenderer<DashboardTileId>).render(data as DashboardTileDataOf<DashboardTileId>, actions);

export const tileSkeleton = (id: DashboardTileId): ReactNode => TILE_RENDERERS[id].skeleton();
export const tileDrillReach = (id: DashboardTileId): DrillReach => TILE_RENDERERS[id].drillReach;

/** A tile with no renderer is one this build does not know — see `hasRenderer` at the page. */
export const hasRenderer = (id: string): id is DashboardTileId => id in TILE_RENDERERS;

export const tileIsDrillable = (id: DashboardTileId, data: unknown): boolean => {
  const renderer = TILE_RENDERERS[id] as TileRenderer<DashboardTileId>;
  if (!renderer.drillable || data === null) return data !== null;
  return renderer.drillable(data as DashboardTileDataOf<DashboardTileId>);
};
